import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import Button from "../../components/button";
import Footer from "../../components/footer";
import Header from "../../components/header";
import Loader from "../../components/loader";
import AISkeleton from "../../components/skeleton/AISkeleton";
import { useTranslate } from "../../context/TranslationContext";
import { useGetContentQuery } from "../../redux/Apis/Content";
import {
  useCreatePlannerMutation,
  useGetPlannerQuery,
} from "../../redux/Apis/Planner";
import { useCreateAISuggestionMutation } from "../../redux/Apis/TrackLecture";
import { formatAISuggestion } from "../../utils/helper";
import "./style.css";
dayjs.extend(isSameOrBefore);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const SustainablePlanner = () => {
  const navigate = useNavigate();
  const { t } = useTranslate("planner");
  const lang = useSelector((state) => state.language.lang);
  const location = useLocation();
  const isMobile = window.innerWidth < 768;
  const { user } = useSelector((state) => state.authSlice);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(
    location?.state?.planner_type || "daily",
  );
  const courseState = location?.state || {};
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;
  // const templateId = courseState?.templateId;
  // const isCoursePlanner = Boolean(courseId && sectionId && templateId);

  const [ratings, setRatings] = useState({
    movement: t("great"),
    nutrition: t("great"),
    sleep: t("great"),
    wellBeing: t("great"),
    focus: t("great"),
    learning: t("great"),
    recovery: t("great"),
    mindset: t("great"),
  });

  const [weeklyTrends, setWeeklyTrends] = useState({
    movement: [],
    nutrition: [],
    sleep: [],
    wellBeing: [],
    focus: [],
    learning: [],
    recovery: [],
    mindset: [],
  });
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [poorCount, setPoorCount] = useState({ sleep: 0 });
  const [dateRange, setDateRange] = useState({
    start: dayjs().subtract(6, "day"),
    end: dayjs(),
  });
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const aiRef = useRef(null);
  const [highlightAI, setHighlightAI] = useState(false);
  const startdate = dateRange.start.format("YYYY-MM-DD");
  const enddate = dateRange.end.format("YYYY-MM-DD");
  const [createPlanner] = useCreatePlannerMutation();
  const { data, refetch } = useGetPlannerQuery(
    { startdate, enddate, lang },
    { skip: !user },
  );
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const normalizeKey = (key) => key.toLowerCase().replace("-", "").trim();
  const isProductivity = selectedCategory === "productivity";
  const categoryOptions = [
    { value: "daily", label: t("dailyOption") },
    { value: "productivity", label: t("productivityOption") },
  ];

  const { data: planVideos, isLoading: isPlanVideosLoading } =
    useGetContentQuery({
      search: "fitness_planner",
    });

  const plannerVideoUrl =
    planVideos?.data?.sections?.find((s) => s.title === "Planner")?.content ||
    "";

  useEffect(() => {
    if (aiLoading) {
      const timer = setTimeout(() => setShowSkeleton(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [aiLoading]);

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });
        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (user && refetch) {
      refetch();
    }
  }, [startdate, enddate, refetch, user]);

  useEffect(() => {
    if (!data) return;

    const transformRatings = (entries = []) => {
      const dayToValue = {};

      entries.forEach((entry) => {
        const day = dayjs(entry.createdAt).format("ddd");

        let value = null;
        if (entry.great) value = 2;
        else if (entry.good) value = 1;
        else if (entry.poor) value = 0;

        if (
          dayToValue[day] === undefined ||
          (value !== null && value > dayToValue[day])
        ) {
          dayToValue[day] = value;
        }
      });

      return labels.map((dayLabel) => {
        return dayToValue[dayLabel] ?? null;
      });
    };

    setWeeklyTrends({
      movement: transformRatings(data?.movement),
      nutrition: transformRatings(data?.nutrition),
      sleep: transformRatings(data?.sleep),
      wellBeing: transformRatings(data?.wellbeing),
      focus: transformRatings(data?.focus),
      learning: transformRatings(data?.learning),
      recovery: transformRatings(data?.recovery),
      mindset: transformRatings(data?.mindset),
    });

    const poorSleepDays =
      data?.sleep?.filter((entry) => entry.poor)?.length || 0;
    setPoorCount((prev) => ({ ...prev, sleep: poorSleepDays }));
  }, [data]);

  const ratingKeyMap = {
    [t("great")]: "great",
    [t("good")]: "good",
    [t("poor")]: "poor",
  };

  const pillars = isProductivity
    ? [
        {
          key: "focus",
          label: t("focus.label"),
          question: t("focus.question"),
        },
        {
          key: "learning",
          label: t("learning.label"),
          question: t("learning.question"),
        },
        {
          key: "recovery",
          label: t("recovery.label"),
          question: t("recovery.question"),
        },
        {
          key: "mindset",
          label: t("mindset.label"),
          question: t("mindset.question"),
        },
      ]
    : [
        {
          key: "movement",
          label: t("movement.label"),
          question: t("movement.question"),
        },
        {
          key: "nutrition",
          label: t("nutrition.label"),
          question: t("nutrition.question"),
        },
        {
          key: "sleep",
          label: t("sleep.label"),
          question: t("sleep.question"),
        },
        {
          key: "wellBeing",
          label: t("wellbeing.label"),
          question: t("wellbeing.question"),
        },
      ];

  const isTodayRated = (entries = []) => {
    const today = dayjs().format("YYYY-MM-DD");
    return entries.some(
      (entry) => dayjs(entry.createdAt).format("YYYY-MM-DD") === today,
    );
  };

  const movementRatedToday = isTodayRated(data?.movement);
  const nutritionRatedToday = isTodayRated(data?.nutrition);
  const sleepRatedToday = isTodayRated(data?.sleep);
  const wellBeingRatedToday = isTodayRated(data?.wellbeing);
  const focusRatedToday = isTodayRated(data?.focus);
  const learningRatedToday = isTodayRated(data?.learning);
  const recoveryRatedToday = isTodayRated(data?.recovery);
  const mindsetRatedToday = isTodayRated(data?.mindset);

  const generateAICoach = async ({ updatedData, pillar, value }) => {
    try {
      // if (!isCoursePlanner) return;

      setAiLoading(true);

      // get latest entry of this pillar
      const pillarKey = pillar.toLowerCase();
      const pillarData = updatedData?.[pillarKey];

      if (!pillarData || pillarData.length === 0) {
        setAiLoading(false);
        return;
      }

      const latest = [...pillarData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];

      let resultStatus = "Good";
      if (latest.poor) resultStatus = "Poor";
      if (latest.great) resultStatus = "Great";

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: isProductivity ? "Productivity Planner" : "Fitness Planner",
        result: [
          {
            _id: latest._id,
            type: pillar,
            input: value,
            result: resultStatus,
          },
        ],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.error("AI suggestion failed:", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRatingChange = async (pillar, value) => {
    if (!user) {
      toast.warning(t("loginFirst"));
      return;
    }

    setRatings((prev) => ({ ...prev, [pillar]: value }));

    const serverKey = ratingKeyMap[value];

    const payload = {
      type: pillar
        .toLowerCase()
        .replace(/([A-Z])/g, "-$1")
        .toLowerCase(),
      [serverKey]: true,
    };

    try {
      if (courseId) payload.course = courseId;

      // create planner
      await createPlanner({ payload, params: { lang } }).unwrap();

      toast.success(`${pillar} ${t("ratingUpdated")}`);

      // refetch planner data
      const updated = await refetch().unwrap();

      // Run AI separately
      // if (isCoursePlanner) {
      await generateAICoach({
        updatedData: updated,
        pillar,
        value,
      });
      // }
    } catch (error) {
      console.error("Planner creation failed:", error);
      setAiLoading(false);
    }
  };

  const generateDateLabels = (start, end) => {
    const labels = [];
    let curr = dayjs(start);
    const last = dayjs(end);

    while (curr.isSameOrBefore(last)) {
      labels.push(curr.format("ddd"));
      curr = curr.add(1, "day");
    }

    return labels;
  };

  const labels = generateDateLabels(startdate, enddate);

  const chartData = (data, label, color) => ({
    labels,
    datasets: [
      {
        label,
        data,
        borderColor: color,
        backgroundColor: `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(
          color.slice(3, 5),
          16,
        )}, ${parseInt(color.slice(5, 7), 16)}, 0.2)`,
        tension: 0.1,
        spanGaps: true,
      },
    ],
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const labelMap = [t("poor"), t("good"), t("great")];
            const value = context.parsed.y;
            return `Status: ${labelMap[value] ?? "Not Rated"}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 2,
        ticks: {
          stepSize: 1,
          callback: (value) => [t("poor"), t("good"), t("great")][value] ?? "",
        },
      },
    },
  };

  const suggestions = isProductivity
    ? {
        focus: {
          tool: t("focusTracker"),
          buttonLabel: `${t("use")} ${t("focus.label")} ${t("tool")}`,
        },
        learning: {
          tool: t("skillBuilder"),
          buttonLabel: `${t("use")} ${t("learning.label")} ${t("tool")}`,
        },
        recovery: {
          tool: t("breakReminder"),
          buttonLabel: `${t("use")} ${t("recovery.label")} ${t("tool")}`,
        },
        mindset: {
          tool: t("mindfulnessTool"),
          buttonLabel: `${t("use")} ${t("mindset.label")} ${t("tool")}`,
        },
      }
    : {
        movement: {
          tool: t("stepTest"),
          buttonLabel: `${t("use")} ${t("movement.label")} ${t("tool")}`,
        },
        sleep: {
          tool: t("sleepCalculator"),
          buttonLabel: `${t("use")} ${t("sleep.label")} ${t("tool")}`,
        },
        nutrition: {
          tool: t("nutritionCalculator"),
          buttonLabel: `${t("use")} ${t("nutrition.label")} ${t("tool")}`,
        },
        wellbeing: {
          tool: t("balanceTest"),
          buttonLabel: `${t("use")} ${t("wellBeing.label")} ${t("tool")}`,
        },
      };

  const chartPillars = isProductivity
    ? [
        { label: t("focus.label"), data: weeklyTrends.focus, color: "#6f42c1" },
        {
          label: t("learning.label"),
          data: weeklyTrends.learning,
          color: "#ff6384",
        },
        {
          label: t("recovery.label"),
          data: weeklyTrends.recovery,
          color: "#36a2eb",
        },
        {
          label: t("mindset.label"),
          data: weeklyTrends.mindset,
          color: "#ffcd56",
        },
      ]
    : [
        {
          label: t("movement.label"),
          data: weeklyTrends.movement,
          color: "#6f42c1",
        },
        {
          label: t("nutrition.label"),
          data: weeklyTrends.nutrition,
          color: "#ff6384",
        },
        { label: t("sleep.label"), data: weeklyTrends.sleep, color: "#36a2eb" },
        {
          label: t("wellbeing.label"),
          data: weeklyTrends.wellBeing,
          color: "#ffcd56",
        },
      ];

  const highestPoor = Object.entries(data?.highest || {}).filter(
    ([key, value]) => {
      const fitnessKeys = ["movement", "nutrition", "sleep", "wellbeing"];
      const productivityKeys = ["focus", "learning", "recovery", "mindset"];

      return (
        value.status === "poor" &&
        (isProductivity
          ? productivityKeys.includes(normalizeKey(key))
          : fitnessKeys.includes(normalizeKey(key)))
      );
    },
  );

  const pillarRoutes = {
    movement: "/calculators/one-rep-max",
    nutrition: "/calculators/hydration",
    sleep: "/calculators/sleep",
    wellbeing: "/fitness-test/balance",
  };

  const handlePrevWeek = () => {
    setDateRange((prev) => ({
      start: prev.start.subtract(7, "day"),
      end: prev.end.subtract(7, "day"),
    }));
  };

  const handleNextWeek = () => {
    const nextEnd = dateRange.end.add(7, "day");
    if (nextEnd.isAfter(dayjs())) return;
    setDateRange((prev) => ({
      start: prev.start.add(7, "day"),
      end: prev.end.add(7, "day"),
    }));
  };

  const getEmbedUrl = (url, autoplay = true) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      if (
        !urlObj.hostname.includes("youtube.com") &&
        !urlObj.hostname.includes("youtu.be")
      ) {
        return null;
      }
      const videoId = urlObj.searchParams.get("v");
      const listId = urlObj.searchParams.get("list");
      if (listId) {
        return `https://www.youtube.com/embed/videoseries?list=${listId}${
          autoplay ? "&autoplay=1&mute=1" : ""
        }`;
      }
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}${
          autoplay ? "?autoplay=1&mute=1" : ""
        }`;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const embedUrl = getEmbedUrl(plannerVideoUrl, !isMobile);
  if (isPlanVideosLoading) {
    return (
      <>
        <Header />
        <Loader loading={true} />
      </>
    );
  }

  const scrollToTop = () => {
    window.scrollTo({ behavior: "smooth", top: 0 });
  };

  return (
    <>
      <Header />
      <div
        style={{
          minHeight: "100vh",
          color: "#fff",
          padding: isMobile ? "110px 10px" : "150px 100px",
        }}
      >
        {embedUrl && (
          <div className="container mb-5">
            <div className="video-container ratio ratio-16x9 mb-5">
              <iframe
                src={embedUrl}
                title="Planner"
                allow="autoplay; encrypted-media"
                allowFullScreen
                style={{ borderRadius: "12px" }}
              />
            </div>
          </div>
        )}

        <div className="sticky-header-wrapper mb-5">
          <div className="container my-2">
            <div className="d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center gap-3">
              <div style={{ width: "100%", maxWidth: "280px" }}>
                <Select
                  isClearable
                  placeholder={t("selectCategory")}
                  value={categoryOptions.find(
                    (opt) => opt.value === selectedCategory,
                  )}
                  onChange={(option) => {
                    if (option) {
                      setSelectedCategory(option.value);
                    }
                  }}
                  options={categoryOptions}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "6px",
                      minHeight: "38px",
                      fontSize: "14px",
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: isSelected
                        ? "var(--primaryColor)"
                        : isFocused
                          ? "#e9ecef"
                          : "#f8f9fa",
                      color: isSelected ? "#fff" : "#000",
                      cursor: "pointer",
                      ":active": {
                        ...base[":active"],
                        backgroundColor: "#0056b3",
                      },
                    }),
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-9 mx-auto text-center">
            {/* {isProductivity ? (
              <>
                <h3 className="mb-3 text-uppercase text-white">
                  {t("productivityTitle")}
                </h3>
                <p className="mb-0 l-grey-text">{t("productivityDesc")}</p>
              </>
            ) : (
              <>
                <h3 className="mb-3 text-uppercase text-white">
                  {t("dailyTitle")}
                </h3>
                <p className="mb-0 l-grey-text">{t("dailyDesc")}</p>
              </>
            )} */}
            <h3 className="mb-3 text-uppercase text-white">{t("title")}</h3>
            <p className="mb-0 l-grey-text">{t("description")}</p>
          </div>
        </div>

        <div className="container mt-4">
          {pillars.map(({ key, label, question }) => {
            const ratedToday =
              key === "movement"
                ? movementRatedToday
                : key === "nutrition"
                  ? nutritionRatedToday
                  : key === "sleep"
                    ? sleepRatedToday
                    : key === "wellBeing"
                      ? wellBeingRatedToday
                      : key === "focus"
                        ? focusRatedToday
                        : key === "learning"
                          ? learningRatedToday
                          : key === "recovery"
                            ? recoveryRatedToday
                            : key === "mindset"
                              ? mindsetRatedToday
                              : false;

            return (
              // <div
              //   key={key}
              //   style={{
              //     backgroundColor: "#2c2c2c",
              //     padding: "20px",
              //     borderRadius: "10px",
              //     marginBottom: "15px",
              //     display: isMobile ? "block" : "flex",
              //     justifyContent: "space-between",
              //   }}
              // >
              //   <div>
              //     <h4
              //       className="text-uppercase"
              //       style={{ margin: "0 0 10px 0" }}
              //     >
              //       {label}
              //     </h4>
              //     <p style={{ color: "#ccc", margin: "0 0 10px 0" }}>
              //       {question}
              //     </p>
              //   </div>

              //   <div className="text-end">
              //     {[t("great"), t("good"), t("poor")].map((val) => (
              //       <Button
              //         key={val}
              //         text={val}
              //         disabled={ratedToday}
              //         className={`ms-2 mt-2 ${
              //           ratedToday
              //             ? "disabled-button"
              //             : ratings[key] === val
              //             ? "site-btn"
              //             : "site-btn border-btn"
              //         }`}
              //         onClick={() => handleRatingChange(key, val)}
              //       />
              //     ))}
              //   </div>
              // </div>
              <div key={key} className="rating-card">
                <div>
                  <h4 style={{ margin: "0 0 10px 0" }}>{label}</h4>
                  <p style={{ color: "#ccc", margin: "0 0 10px 0" }}>
                    {question}
                  </p>
                </div>
                <div className="rating-buttons text-end">
                  {[t("great"), t("good"), t("poor")].map((val) => (
                    <Button
                      key={val}
                      text={val}
                      disabled={ratedToday}
                      className={`ms-2 mt-2 ${
                        ratedToday
                          ? "disabled-button"
                          : ratings[key] === val
                            ? "site-btn"
                            : "site-btn border-btn"
                      }`}
                      onClick={() => handleRatingChange(key, val)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-center text-center text-md-start my-4 mt-5">
            <Button text={`← ${t("preWeek")}`} onClick={handlePrevWeek} />
            <p className="mb-0">
              {dateRange.start.format("DD MMM")} –{" "}
              {dateRange.end.format("DD MMM")}
            </p>
            <Button
              text={`${t("nextWeek")} →`}
              onClick={handleNextWeek}
              disabled={dateRange.end.isSame(dayjs(), "day")}
            />
          </div>

          <div
            style={{
              borderRadius: "10px",
              marginBottom: "15px",
            }}
          >
            <h4 className="my-3">{t("weekTitle")}</h4>
            <div className="row g-3">
              {chartPillars.map(({ label, data, color }, index) => (
                <div key={index} className="col-12 col-sm-6 col-lg-3">
                  <div
                    style={{
                      backgroundColor: "#2c2c2c",
                      borderRadius: "10px",
                      padding: "12px",
                      height: "100%",
                    }}
                  >
                    <p className="fw-bold">{label}</p>
                    <div style={{ height: "200px" }}>
                      <Line
                        data={chartData(data, label, color)}
                        options={chartOptions}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {highestPoor.length > 0 && (
            <div
              style={{
                backgroundColor: "#2c2c2c",
                padding: "20px",
                borderRadius: "10px",
                marginBottom: "15px",
              }}
            >
              <h4 style={{ margin: "0 0 10px 0" }}>{t("needSupport")}</h4>

              {highestPoor.map(([pillar, value], idx) => {
                const key = normalizeKey(pillar);
                const suggestion = suggestions[key];
                const buttonLabel = suggestion?.buttonLabel || t("viewTool");
                const route = pillarRoutes[key] || "/calculators";

                return (
                  <div key={pillar}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <p style={{ color: "#ccc", margin: 0 }}>
                        {t("yourRated")}{" "}
                        <strong>
                          {pillar.charAt(0).toUpperCase() + pillar.slice(1)}
                        </strong>{" "}
                        {t("as")} <strong>{t("poor")}</strong> {value.count}{" "}
                        {t("help")}
                      </p>

                      <Button
                        text={
                          <>
                            <FontAwesomeIcon icon={faCircleQuestion} />{" "}
                            {buttonLabel}
                          </>
                        }
                        onClick={() => navigate(route)}
                      />
                    </div>
                    {idx !== highestPoor.length - 1 && <hr />}
                  </div>
                );
              })}
            </div>
          )}

          {(aiLoading || aiSuggestion) && (
            <div
              ref={aiRef}
              className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
            >
              <h4 style={{ marginBottom: "10px" }}>
                {/* <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "} */}
                <img
                  src="https://traineego.com/assets/images/favicon-2026.ico"
                  alt="AI Coach"
                  style={{
                    width: "45px",
                    marginRight: "8px",
                    borderRadius: "25px",
                  }}
                />
                {t("aiSuggestion.title")}
              </h4>

              {showSkeleton ? (
                // <p>
                //   <Spinner size="sm" /> {t("aiSuggestion.loading")}
                // </p>
                <AISkeleton />
              ) : (
                <div
                  style={{
                    color: "#ccc",
                    lineHeight: "1.7",
                    fontSize: "15px",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatAISuggestion(aiSuggestion),
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="btn purple-text rounded-circle shadow"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1050,
            width: "50px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="bi bi-arrow-up-circle-fill fs-3"></i>
        </button>
      )}
    </>
  );
};

export default SustainablePlanner;
